/**
 * Coach SSE sentinel contract.
 *
 * The AI OKR Coach emits zero or more trailing sentinel lines at the very
 * end of its streamed response. `extractSignals` strips those lines from
 * the visible body and exposes the parsed values.
 *
 * Contract:
 *   READINESS: ready | not_ready | ready_with_caveats
 *   RETURN_TO_OBJECTIVE: true                        (KR coach only, optional)
 *   MODE_RECALIBRATED: novice | developing | fluent  (optional, silent)
 *
 * Each sentinel must occupy its own line at the end of the stream. Sentinel
 * tokens that appear mid-paragraph — i.e. not on their own line — are
 * treated as user/model prose and are NOT matched.
 *
 * `extractSignals` is guaranteed never to throw. On any malformed input it
 * fails open and returns `{ visible: input }` with no parsed values. This
 * keeps the wizard in coaching mode when the model omits the contract.
 */

export type Readiness = "ready" | "not_ready" | "ready_with_caveats";
export const FAMILIARITY_LEVELS = ["novice", "developing", "fluent"] as const;
export type Familiarity = (typeof FAMILIARITY_LEVELS)[number];

export interface ExtractedSignals {
  readonly visible: string;
  readonly readiness?: Readiness;
  readonly returnToObjective?: true;
  readonly modeRecalibrated?: Familiarity;
}

/** Readiness sentinel — must be on its own line. Tolerates **READINESS:** markdown. */
export const READINESS_REGEX =
  /^\s*\*{0,2}READINESS:\*{0,2}\s*(ready_with_caveats|not_ready|ready)\s*$/i;

/** Return-to-objective sentinel — must be on its own line. */
export const RETURN_TO_OBJECTIVE_REGEX =
  /^\s*\*{0,2}RETURN_TO_OBJECTIVE:\*{0,2}\s*true\s*$/i;

/** Silent mode-downshift sentinel — must be on its own line. */
export const MODE_RECALIBRATED_REGEX =
  /^\s*\*{0,2}MODE_RECALIBRATED:\*{0,2}\s*(novice|developing|fluent)\s*$/i;

/** Required sentinel-contract phrases for admin prompt validation. */
export const COACH_SIGNAL_REQUIRED_PHRASES: readonly string[] = [
  "READINESS:",
  "RETURN_TO_OBJECTIVE:",
  "MODE_RECALIBRATED:",
] as const;

/**
 * Phrases required on every coach prompt (Objective and KR).
 * Single source of truth for the admin validator (server) and the admin
 * prompt editor (client).
 */
export const COACH_REQUIRED_PHRASES_BOTH: readonly string[] = [
  "READINESS:",
  "MODE_RECALIBRATED:",
] as const;

/** Phrases required only on the KR coach prompt. */
export const COACH_REQUIRED_PHRASES_KR_ONLY: readonly string[] = [
  "RETURN_TO_OBJECTIVE:",
] as const;

export type CoachPromptKind = "objective" | "kr";

/** Phrases required for a given prompt kind, composed from the groups above. */
export function coachRequiredPhrasesFor(
  kind: CoachPromptKind,
): readonly string[] {
  return kind === "kr"
    ? [...COACH_REQUIRED_PHRASES_BOTH, ...COACH_REQUIRED_PHRASES_KR_ONLY]
    : COACH_REQUIRED_PHRASES_BOTH;
}

/**
 * Return the first required phrase missing from `prompt` for the given
 * prompt kind, or null if all required phrases are present. Used by the
 * admin validator (server) and the admin prompt editor (client) so the two
 * agree on exactly which phrases are required.
 */
export function findMissingCoachPhrase(
  prompt: string,
  kind: CoachPromptKind,
): string | null {
  for (const phrase of coachRequiredPhrasesFor(kind)) {
    if (!prompt.includes(phrase)) return phrase;
  }
  return null;
}

function matchReadiness(line: string): Readiness | null {
  const m = READINESS_REGEX.exec(line);
  if (!m) return null;
  const raw = m[1]?.toLowerCase();
  if (raw === "ready" || raw === "not_ready" || raw === "ready_with_caveats") {
    return raw;
  }
  return null;
}

function matchFamiliarity(line: string): Familiarity | null {
  const m = MODE_RECALIBRATED_REGEX.exec(line);
  if (!m) return null;
  const raw = m[1]?.toLowerCase();
  if (raw === "novice" || raw === "developing" || raw === "fluent") {
    return raw;
  }
  return null;
}

interface ParsedSignals {
  readiness?: Readiness;
  returnToObjective?: true;
  modeRecalibrated?: Familiarity;
}

/** Walks `lines` backwards, popping sentinel and blank lines. Mutates the array. */
function consumeTrailingLines(lines: string[]): ParsedSignals {
  const signals: ParsedSignals = {};
  while (lines.length > 0) {
    const raw = lines[lines.length - 1];
    if (raw === undefined) break;
    const line = raw.trim();
    if (line === "") { lines.pop(); continue; }
    if (signals.readiness === undefined) {
      const hit = matchReadiness(raw);
      if (hit) { signals.readiness = hit; lines.pop(); continue; }
    }
    if (signals.returnToObjective === undefined && RETURN_TO_OBJECTIVE_REGEX.test(raw)) {
      signals.returnToObjective = true; lines.pop(); continue;
    }
    if (signals.modeRecalibrated === undefined) {
      const hit = matchFamiliarity(raw);
      if (hit) { signals.modeRecalibrated = hit; lines.pop(); continue; }
    }
    break;
  }
  return signals;
}

/**
 * Extract trailing sentinel lines from a coach response.
 *
 * Walks backwards from the end of the text, consuming at most one of each
 * sentinel type. Stops as soon as a non-sentinel, non-blank line appears.
 * The remaining text (with trailing whitespace trimmed) is the `visible` body.
 *
 * Never throws. On any unexpected input shape returns `{ visible: input }`.
 */
export function extractSignals(text: unknown): ExtractedSignals {
  if (typeof text !== "string") {
    return { visible: "" };
  }
  try {
    const lines = text.split(/\r?\n/);
    const { readiness, returnToObjective, modeRecalibrated } = consumeTrailingLines(lines);
    const visible = lines.join("\n").replace(/\s+$/, "");
    return {
      visible,
      ...(readiness !== undefined ? { readiness } : {}),
      ...(returnToObjective !== undefined ? { returnToObjective } : {}),
      ...(modeRecalibrated !== undefined ? { modeRecalibrated } : {}),
    };
  } catch {
    return { visible: text };
  }
}
